import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import {
  certificateSubject,
  downloadDocument,
  downloadDocuments,
  getCertificate,
  sendToSftpPost,
  useCertificate,
} from "../hooks/certificate";
import { useHistory, useLocation } from "react-router-dom";
import QRCode from "qrcode";
import { Button, Col, Form, Input, Modal, Row, Spin, Typography } from "antd";
import { CertificateDto, CertificateFileDto } from "../generated-api";
import {
  PlusCircle,
  CheckCircle,
  Circle,
  Download,
  File,
  GitBranch,
  Hash,
  Printer,
  Upload,
  Slash,
  Loader,
} from "react-feather";
import fileDownload from "js-file-download";
import { NotDefined } from "../components/NotDefined";
import { ResponseSubjectState } from "../hooks";
import { printLabel, saveLabel } from "../utils/pdf";
import { hasAnyRole, isAuthenticated, permissions } from "../hooks/user";
import { BackButton } from "../components/layout/BackButton";
import { renderDate } from "../utils/render";
import { TEMPLATE_PARAM_DATE } from "../constants/template";
import tracertStamp from "../svg/tracert_stamp.svg";
import { showSuccess } from "../components/Notification";

const { REACT_APP_API_DOMAIN } = process.env;
type tpDownloadAnimation = {
  [key: string]: boolean;
};

export function CertificatePage({ match }: any) {
  const { certificateId } = (match || {}).params || {};
  const [downloadAnimation, setDownloadAnimation] =
    useState<tpDownloadAnimation>({});
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [sftpRefModalVisible, setSftpRefModalVisible] = useState(false); 
  const [sftpRefValue, setSftpRefValue] = useState('');
  const certificateResponse = useCertificate();
  const certificate: CertificateDto = certificateResponse.data;
  const queryParams = new URLSearchParams(location.search);

  useEffect(() => {
    if (certificateId) {
      if (
        !certificateResponse.data ||
        certificateResponse.data.id !== certificateId
      ) {
        certificateSubject.next(new ResponseSubjectState());
        getCertificate(certificateId, true)
          .then()
          .catch((error) => {
            console.error(error);
            history.push("/certificates");
          });
      }
      const canvas = document.getElementById("qrcode_" + certificateId);
      if (canvas) {
        const data =
          window.location.protocol +
          "//" +
          window.location.host +
          "/certificates/" +
          certificateId;
        QRCode.toCanvas(canvas, data, console.error);
      }
    } else {
      history.push("/certificates");
    }
  }, [certificateId]);

  const startDownloadAnimation = (id: string): void => {
    setDownloadAnimation({ ...downloadAnimation, [id]: true });
  };

  const endDownloadAnimation = (id: string): void => {
    setDownloadAnimation({ ...downloadAnimation, [id]: false });
  };

  const downloadOneDocument = (
    certificateId: string,
    file: CertificateFileDto
  ): void => {
    const animationId = file.id ? file.id : file.name;
    startDownloadAnimation(animationId);
    downloadDocument(certificateId, file.id as string)
      .then((response) => response.blob())
      .then((response) => fileDownload(response, file.name as string))
      .catch(console.error)
      .finally(() => endDownloadAnimation(animationId));
  };

  const downloadAllDocuments = (certificateId: string): void => {
    const animationId = certificateId;
    startDownloadAnimation(animationId);
    downloadDocuments(certificateId)
      .then((response) => response.blob())
      .then((response) => fileDownload(response, certificateId + ".zip"))
      .catch(console.error)
      .finally(() => endDownloadAnimation(animationId));

    saveQRCode();
  };

  const askForSftpReferenceNumber = (certificateId: string): void => {
   setSftpRefModalVisible(true);
  }

  const closeAskForSftpReferenceNumber = (): void => {
    setSftpRefModalVisible(false);
    setSftpRefValue('');
   }

  const sendToSftp = (certificateId: string): void => {
    sendToSftpPost(certificateId, sftpRefValue)
      .then(() => { 
        closeAskForSftpReferenceNumber();
        showSuccess(t('general.success'), t('certificate.sftp.success')) 
      })
      .catch(console.error)
  }

  const createAnotherCertificate = (): void => {
    history.push(
      `/certificates/add?businessSector=${queryParams.get(
        "businessSector"
      )}&template=${certificate.templateId}`
    );
  };

  const printQRCode = () => {
    const canvas: any = document.getElementById("qrcode_" + certificateId);
    if (canvas) {
      printLabel(canvas.toDataURL(), certificate);
    }
  };

  const saveQRCode = () => {
    const canvas: any = document.getElementById("qrcode_" + certificateId);
    if (canvas) {
      saveLabel(canvas.toDataURL(), certificate);
    }
  };

  const files = (certificate || {}).files || [];
  const parentCertificates = (certificate || {}).parentCertificates || [];
  const parameterValues = (certificate || {}).parameterValues || [];
  const { blockChainHashes, certValid } = certificate || {};
  const blockChainHash = (blockChainHashes || {}).certificateHash;

  const canViewDocuments = hasAnyRole(permissions.certificateDocuments);
  const canDownloadDocuments = hasAnyRole(
    permissions.downloadCertificateDocuments
  );

  return (
    <Spin spinning={certificateResponse.loading}>
      <PageHeader />
      <div className={"top-block accent4-background"}>
        <div className={"flex-row"}>
          <h1 className={"h1 flex-auto"}>
            <BackButton
              backPath={isAuthenticated() ? "/certificates" : "/dashboard"}
            />
            <File />
            {t("certificate.title")}
          </h1>
          <a
            href={"/certificates/" + certificateId + "/map"}
            className={"title-icon-button"}
            onClick={(event) => {
              event.preventDefault();
              history.push("/certificates/" + certificateId + "/map");
            }}
          >
            <Hash />
            <div className={"title-icon-button-text"}>
              {t("certificate.map")}
            </div>
          </a>
        </div>
        <Form labelCol={{ span: 11 }} className={"p-0"}>
          <Row className={"row-offset"}>
            <Col span={24}>
              <div className={"flex-row mb-05"}>
                <GitBranch className={"input-row-icon h-auto"} />
                <div className={"flex-auto overflow-ellipsis"}>
                  <div className={"ant-form-item-label"}>
                    <label className={"h-05"}>
                      {t("certificate.blockchainHash")}
                    </label>
                  </div>
                  <div className={"overflow-ellipsis weight-600"}>
                    {blockChainHash || <NotDefined />}
                  </div>
                </div>
              </div>
            </Col>
            {!certificateResponse.loading && (
              <Col span={24}>
                <div
                  className={
                    "flex-row mb-05 " +
                    (certValid
                      ? "ant-typography ant-typography-success"
                      : "ant-typography ant-typography-danger")
                  }
                >
                  {certValid ? (
                    <CheckCircle className={"input-row-icon h-auto"} />
                  ) : (
                    <Slash className={"input-row-icon h-auto"} />
                  )}
                  {certValid ? (
                    <div className={"flex-auto vertical-center"}>
                      {t("certificate.hashesMatch")}
                    </div>
                  ) : (
                    <div className={"flex-auto vertical-center"}>
                      {t("certificate.hashesNotMatch")}
                    </div>
                  )}
                </div>
              </Col>
            )}
            {!!certificateResponse.loading && (
              <Col span={24}>
                <div className={"flex-row mb-05"}>
                  <Circle className={"input-row-icon h-auto"} />
                  <div className={"flex-auto vertical-center"}>&nbsp;</div>
                </div>
              </Col>
            )}
          </Row>
          <Row>
            <Col span={24} className={"qrcode-warpper"}>
              <img src={tracertStamp} className={"qrstamp"} alt='qrstamp'/>
              <canvas id={"qrcode_" + certificateId} className={"qrcode"}/>
            </Col>
          </Row>
          <Row>
            {parameterValues.map(
              (parameter) =>
                !!parameter.value && (
                  <Col
                    key={parameter.templateParameterId}
                    span={24}
                    md={12}
                    xl={8}
                  >
                    <Form.Item label={parameter.name}>
                      {parameter.type !== TEMPLATE_PARAM_DATE ? (
                        <Input value={parameter.value} readOnly />
                      ) : (
                        <Input value={renderDate(parameter.value)} readOnly />
                      )}
                    </Form.Item>
                  </Col>
                )
            )}
          </Row>
        </Form>
      </div>
      <div className={"subpage-content"}>
        {
          !isAuthenticated() 
          ?  <div className="pb-1 italic">
              Only registered users are able to see the documents here. If you want to become a registered user, please visit: <a href='https://cert-trace.com'>www.cert-trace.com</a>
            </div>
        : ''
        }
        {!!canViewDocuments && !!files.length && (
          <Row className={"row-margin-bottom"}>
            <Col span={24}>
              <h2 className={"text-default"}>{t("certificate.files")}</h2>
              {files.map((file) => (
                <div key={file.id} className={"mb-05"}>
                  {!!canDownloadDocuments && (
                    <a
                      href={
                        REACT_APP_API_DOMAIN +
                        `/api/Certificate/${certificateId}/document/${file.id}`
                      }
                      className={"row-link"}
                      onClick={(event) => {
                        event.preventDefault();
                        downloadOneDocument(certificateId, file);
                      }}
                    >
                      <div className="text-with-icon">
                        {downloadAnimation[file.id ? file.id : file.name] !==
                        true ? (
                          <Download />
                        ) : (
                          <Loader className="animation-spin" />
                        )}{" "}
                        {file.name}
                      </div>
                    </a>
                  )}
                  {!canDownloadDocuments && <span>{file.name}</span>}
                </div>
              ))}
            </Col>
          </Row>
        )}
        {!!parentCertificates.length && (
          <Row className={"row-margin-bottom"}>
            <Col span={24}>
              <h2 className={"text-default"}>{t("certificate.links")}</h2>
              {parentCertificates.map((link) => (
                <div key={link.id} className={"mb-05"}>
                  <a
                    href={"/certificates/" + link.id}
                    className={"row-link"}
                    onClick={(event) => {
                      event.preventDefault();
                      history.push("/certificates/" + link.id);
                    }}
                  >
                    <b>{link.title}</b> {link.hash}
                  </a>
                </div>
              ))}
            </Col>
          </Row>
        )}
        <div className={"flex-row"}>
          <div className={"flex-auto"}>
            <Button
              className={
                "ant-btn-secondary button-with-icon accent4-background text-white mr-01"
              }
              onClick={() => printQRCode()}
            >
              <Printer /> {t("certificate.printQR")}
            </Button>
          </div>
          <div className={"flex-row"}>
            {!!canDownloadDocuments && !!files.length ? (
              <Button
                type="primary"
                className={
                  "accent4-background button-with-icon text-white ml-01"
                }
                onClick={() => downloadAllDocuments(certificateId)}
              >
                {downloadAnimation[certificateId] !== true ? (
                  <Download />
                ) : (
                  <Loader className="animation-spin" />
                )}{" "}
                {t("general.downloadAll")}
              </Button>
            ) : (
              ""
            )}
            {queryParams.get("businessSector") ? (
              <Button
                type="primary"
                className={
                  "accent4-background button-with-icon text-white ml-01"
                }
                onClick={() => createAnotherCertificate()}
              >
                <PlusCircle /> Next
              </Button>
            ) : (
              ""
            )}
          </div>
        </div>
        {certificate && certificate.canSendToSftp ? (
          <div className={"flex-row pt-1"}>
            <Button
              className={
                "ant-btn-secondary button-with-icon accent4-background text-white mr-01 w-100"
              }
              onClick={() => askForSftpReferenceNumber(certificateId)}
            >
              <Upload /> {t("certificate.sftp.sendToSftp")}
            </Button>
            <Modal 
                title={t('certificate.sftp.referenceModalHeader')}
                okType={'primary'}
                okText={t('general.ok')}
                visible={sftpRefModalVisible}
                centered
                cancelText={t('general.cancel')}
                onOk={() => sendToSftp(certificateId)}
                okButtonProps={{ disabled: sftpRefValue.trim().length === 0 }}
                onCancel={() => closeAskForSftpReferenceNumber()}
            >
              <>
                <Form.Item label={t('certificate.sftp.referenceValue')}>
                 <Input onChange={(e) => { setSftpRefValue(e.target.value.slice(0,16))} } value={sftpRefValue}></Input>
                </Form.Item>
                <Typography.Text type="secondary"><small>{t('certificate.sftp.referenceModalHelp')}</small></Typography.Text>
              </>
            </Modal> 
          </div>
        ) : (
          ""
        )}
      </div>
    </Spin>
  );
}
